import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, ScrollView, ActivityIndicator, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import { Tooltip } from '@/components/General/Tooltip';
import usePlatform from '@/utils/hooks/usePlatform';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getLocationScheduleForDay } from '@/data/events';

const HOUR_WIDTH = 96;
const LABEL_WIDTH = 140;
const DAY_HOURS = 24;
const HEADER_HEIGHT = 32;
const LANE_HEIGHT = 30;
const LANE_GAP = 4;
const ROW_VPAD = 6;
const BLOCK_MIN_WIDTH = 14;
const DEFAULT_SCROLL_HOUR = 6;
const SCROLL_STEP = HOUR_WIDTH * 4;

const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};

const formatHour = (h) => {
    const d = new Date();
    d.setHours(h, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric' });
};

const formatTime = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const formatDayShort = (d) => d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

const rowHeightFor = (laneCount) => laneCount * LANE_HEIGHT + Math.max(laneCount - 1, 0) * LANE_GAP + ROW_VPAD * 2;

/**
 * Room schedule ("gantt") modal: locations as vertical rows, hours of a single
 * day as horizontal columns, booked event instances as blocks. Overlapping
 * bookings in the same room stack into lanes; the row grows to fit them.
 *
 * The hour header is pinned above a vertical ScrollView that holds both the
 * fixed label column and the horizontally scrolling track, so it stays put
 * while rows scroll. A second (non-interactive) horizontal ScrollView in the
 * header is synced to the track via scrollTo.
 *
 * In picker mode (`selectable`) tapping a location row calls
 * `onToggleLocation(location)` and selected rows are highlighted. The draft
 * event's time span is painted as a full column behind the blocks on every
 * row via `highlightStart`/`highlightEnd`/`highlightAllDay`.
 *
 * Tapping a location name pushes `/locations/[id]` — the modal stays open
 * (`stayOpenOnBlur`) so focus returns to it afterwards.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onClose
 * @param {Date|string} [props.date] - initial day shown (defaults to today)
 * @param {string[]} [props.selectedLocationIDs] - highlighted location ids
 * @param {Function} [props.onToggleLocation] - called with the Location row on tap
 * @param {boolean} [props.selectable] - enable tap-to-select rows
 * @param {Date|string} [props.highlightStart] - draft event start; painted on selected rows
 * @param {Date|string} [props.highlightEnd] - draft event end (defaults to start + 1h)
 * @param {boolean} [props.highlightAllDay] - paint the whole day on selected rows
 */
const LocationScheduleModal = ({ visible, onClose, date, selectedLocationIDs = [], onToggleLocation, selectable = false, highlightStart = null, highlightEnd = null, highlightAllDay = false }) => {
    const router = useRouter();
    const { isWeb } = usePlatform();
    const { height: windowHeight } = useWindowDimensions();
    const [currentDate, setCurrentDate] = useState(() => (date ? new Date(date) : new Date()));

    const trackRef = useRef(null);
    const headerRef = useRef(null);
    const scrollXRef = useRef(DEFAULT_SCROLL_HOUR * HOUR_WIDTH);
    const trackViewportRef = useRef(0);

    // Re-seed the displayed day every time the modal is opened.
    useEffect(() => {
        if (visible) {
            setCurrentDate(date ? new Date(date) : new Date());
        }
    }, [visible, date]);

    const dayStart = startOfDay(currentDate);
    const dayEnd = addDays(dayStart, 1);
    const dayKey = dayStart.toISOString();
    const trackWidth = DAY_HOURS * HOUR_WIDTH;
    // Bounded height so the inner vertical ScrollView (not the SheetModal's
    // own scroll container) scrolls the rows — that's what keeps the hour
    // header pinned.
    const bodyMaxHeight = Math.max(220, windowHeight - 280);

    const { data: locations } = Query.locations.all();
    const { data: instances = [], isFetching } = useQuery({
        queryKey: QueryKeys.locations.scheduleForDay(dayKey),
        queryFn: () => getLocationScheduleForDay(currentDate),
        enabled: !!visible,
    });

    // locationID -> booked instances for that room
    const blocksByLocation = useMemo(() => {
        const map = {};
        (instances || []).forEach((inst) => {
            (inst.effectiveLocationIDs || []).forEach((locationID) => {
                if (!locationID) return;
                (map[locationID] = map[locationID] || []).push(inst);
            });
        });
        return map;
    }, [instances]);

    const blockGeometry = (inst) => {
        if (inst.allDay || !inst.startTime) {
            return { left: 0, width: trackWidth, startMs: dayStart.getTime(), endMs: dayEnd.getTime() };
        }
        const s = new Date(inst.startTime);
        const e = inst.endTime ? new Date(inst.endTime) : new Date(s.getTime() + 60 * 60 * 1000);
        const clampedStart = Math.max(s.getTime(), dayStart.getTime());
        const clampedEnd = Math.min(Math.max(e.getTime(), s.getTime() + 15 * 60 * 1000), dayEnd.getTime());
        if (clampedEnd <= dayStart.getTime() || clampedStart >= dayEnd.getTime()) return null;
        return {
            left: ((clampedStart - dayStart.getTime()) / 3600000) * HOUR_WIDTH,
            width: Math.max(((clampedEnd - clampedStart) / 3600000) * HOUR_WIDTH, BLOCK_MIN_WIDTH),
            startMs: clampedStart,
            endMs: clampedEnd,
        };
    };

    // Per-location lane assignment: overlapping blocks stack into lanes so no
    // two bars overlap; laneCount drives the row height.
    const laneDataByLocation = useMemo(() => {
        const map = {};
        Object.entries(blocksByLocation).forEach(([locationID, blocks]) => {
            const items = [];
            blocks.forEach((inst) => {
                const geo = blockGeometry(inst);
                if (geo) items.push({ inst, ...geo, lane: 0 });
            });
            items.sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
            const laneEnds = [];
            items.forEach((item) => {
                let lane = laneEnds.findIndex((end) => end <= item.startMs);
                if (lane === -1) {
                    lane = laneEnds.length;
                    laneEnds.push(0);
                }
                laneEnds[lane] = item.endMs;
                item.lane = lane;
            });
            map[locationID] = { items, laneCount: Math.max(laneEnds.length, 1) };
        });
        return map;
    }, [blocksByLocation, dayKey]);

    // Draft-event highlight rect (minute precision), clamped to the shown day.
    const highlightGeo = useMemo(() => {
        if (!highlightStart) return null;
        const s = new Date(highlightStart);
        if (isNaN(s)) return null;
        if (highlightAllDay) {
            if (startOfDay(s).getTime() !== dayStart.getTime()) return null;
            return { left: 0, width: trackWidth };
        }
        const e = highlightEnd && !isNaN(new Date(highlightEnd)) ? new Date(highlightEnd) : new Date(s.getTime() + 60 * 60 * 1000);
        const clampedStart = Math.max(s.getTime(), dayStart.getTime());
        const clampedEnd = Math.min(Math.max(e.getTime(), s.getTime() + 15 * 60 * 1000), dayEnd.getTime());
        if (clampedEnd <= dayStart.getTime() || clampedStart >= dayEnd.getTime()) return null;
        return {
            left: ((clampedStart - dayStart.getTime()) / 3600000) * HOUR_WIDTH,
            width: Math.max(((clampedEnd - clampedStart) / 3600000) * HOUR_WIDTH, 4),
        };
    }, [highlightStart, highlightEnd, highlightAllDay, dayKey]);

    const scrollToX = (x, animated = false) => {
        const maxX = Math.max(trackWidth - (trackViewportRef.current || 0), 0);
        const next = Math.min(Math.max(x, 0), maxX);
        scrollXRef.current = next;
        trackRef.current?.scrollTo({ x: next, animated });
        headerRef.current?.scrollTo({ x: next, animated: false });
    };

    // Default horizontal position: the 6am mark, on open and on day change.
    useEffect(() => {
        if (!visible) return;
        const t = setTimeout(() => scrollToX(DEFAULT_SCROLL_HOUR * HOUR_WIDTH), 60);
        return () => clearTimeout(t);
    }, [visible, dayKey]);

    const onTrackScroll = (e) => {
        const x = e.nativeEvent.contentOffset.x;
        scrollXRef.current = x;
        headerRef.current?.scrollTo({ x, animated: false });
    };

    const blockName = (inst) => inst.nameOverride || inst.Event?.name || 'Event';

    const blockTooltipSubtitle = (inst) => {
        if (inst.allDay) return 'All Day';
        const s = inst.startTime ? new Date(inst.startTime) : null;
        const e = inst.endTime ? new Date(inst.endTime) : null;
        if (!s) return '';
        const startPart = `${formatDayShort(s)} · ${formatTime(s)}`;
        if (!e) return startPart;
        const sameDay = s.toDateString() === e.toDateString();
        return `${startPart} – ${sameDay ? formatTime(e) : `${formatDayShort(e)} · ${formatTime(e)}`}`;
    };

    const openLocation = (locationID) => {
        router.push({ pathname: '/locations/[locationID]', params: { locationID } });
    };

    const shiftDay = (n) => setCurrentDate((d) => addDays(d, n));

    const selectedSet = useMemo(() => new Set(selectedLocationIDs || []), [selectedLocationIDs]);

    const renderGridlines = (rowH) => Array.from({ length: DAY_HOURS }, (_, h) => (
        <View key={h} style={[styles.gridline, { left: h * HOUR_WIDTH, height: rowH }]} />
    ));

    const renderBlock = ({ inst, left, width, lane }, loc) => {
        const color = inst.Event?.Calendar?.color || undefined;
        return (
            <Tooltip
                key={inst.id}
                testID={`LocationSchedule-Block-${inst.id}`}
                title={blockName(inst)}
                subtitle={blockTooltipSubtitle(inst)}
                onPress={selectable ? () => onToggleLocation?.(loc) : undefined}
                style={[styles.block(color), {
                    left: left + 1,
                    width: Math.max(width - 2, BLOCK_MIN_WIDTH - 2),
                    top: ROW_VPAD + lane * (LANE_HEIGHT + LANE_GAP),
                    height: LANE_HEIGHT,
                }]}
            >
                <Text style={styles.blockText(color)} numberOfLines={1}>
                    {blockName(inst)}
                </Text>
            </Tooltip>
        );
    };

    return (
        <SheetModal
            title="Room Schedule"
            subtitle={dayStart.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            isOpen={visible}
            onCancel={onClose}
            width={1200}
            stayOpenOnBlur
        >
            <View style={styles.container} testID="LocationScheduleModal">
                {/* Day navigation */}
                <View style={styles.navRow}>
                    <Pressable
                        testID="LocationSchedule-PrevDay"
                        style={styles.navButton}
                        onPress={() => shiftDay(-1)}
                        accessibilityRole="button"
                        accessibilityLabel="Previous day"
                    >
                        <Unicon name="chevron-back" size={20} colorVariant="text" />
                    </Pressable>

                    <View style={styles.datePickerWrap}>
                        <DatePickerInput
                            title="Date"
                            mode="date"
                            value={currentDate}
                            hideClearIcon
                            hideLabel
                            onUpdate={(d) => d && setCurrentDate(new Date(d))}
                        />
                    </View>

                    <Pressable
                        testID="LocationSchedule-NextDay"
                        style={styles.navButton}
                        onPress={() => shiftDay(1)}
                        accessibilityRole="button"
                        accessibilityLabel="Next day"
                    >
                        <Unicon name="chevron-forward" size={20} colorVariant="text" />
                    </Pressable>
                </View>

                <View style={styles.metaRow}>
                    {selectable && <Text style={styles.hint}>Tap a room to add or remove it from the event.{highlightGeo ? ' The shaded span is your event\'s selected time.' : ''}</Text>}
                    {isFetching && <ActivityIndicator size="small" />}
                </View>

                {/* Gantt grid */}
                <View style={styles.grid}>
                    {/* Pinned hour header (horizontal position synced to the track) */}
                    <View style={styles.headerRow}>
                        {/* Corner cell doubles as the left scroll control so the
                            hour cells stay x-aligned with the track below. */}
                        <View style={[styles.cornerCell, { width: LABEL_WIDTH, height: HEADER_HEIGHT }]}>
                            <Text style={styles.hourText} numberOfLines={1}>Room</Text>
                            {isWeb && (
                                <Pressable
                                    testID="LocationSchedule-ScrollLeft"
                                    style={styles.cornerArrow}
                                    onPress={() => scrollToX(scrollXRef.current - SCROLL_STEP, true)}
                                    accessibilityRole="button"
                                    accessibilityLabel="Scroll earlier"
                                >
                                    <Unicon name="chevron-back" size={13} colorVariant="textSecondary" />
                                </Pressable>
                            )}
                        </View>
                        <ScrollView
                            ref={headerRef}
                            horizontal
                            scrollEnabled={false}
                            showsHorizontalScrollIndicator={false}
                            contentOffset={isWeb ? undefined : { x: DEFAULT_SCROLL_HOUR * HOUR_WIDTH, y: 0 }}
                            style={styles.headerScroll}
                        >
                            <View style={[styles.hourRow, { height: HEADER_HEIGHT, width: trackWidth }]}>
                                {Array.from({ length: DAY_HOURS }, (_, h) => (
                                    <View key={h} style={[styles.hourCell, { width: HOUR_WIDTH }]}>
                                        <Text style={styles.hourText}>{formatHour(h)}</Text>
                                    </View>
                                ))}
                            </View>
                        </ScrollView>
                        {isWeb && (
                            <Pressable
                                testID="LocationSchedule-ScrollRight"
                                style={[styles.scrollArrow, { height: HEADER_HEIGHT }]}
                                onPress={() => scrollToX(scrollXRef.current + SCROLL_STEP, true)}
                                accessibilityRole="button"
                                accessibilityLabel="Scroll later"
                            >
                                <Unicon name="chevron-forward" size={14} colorVariant="textSecondary" />
                            </Pressable>
                        )}
                    </View>

                    {/* Rows: fixed label column + horizontally scrolling track,
                        both inside one vertical ScrollView so they stay aligned */}
                    <ScrollView
                        style={[styles.bodyScroll, { maxHeight: bodyMaxHeight }]}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        testID="LocationSchedule-BodyScroll"
                    >
                        <View style={styles.bodyRow}>
                            {/* Fixed label column */}
                            <View style={styles.labelCol}>
                                {(locations || []).map((loc) => {
                                    const selected = selectedSet.has(loc.id);
                                    const rowH = rowHeightFor(laneDataByLocation[loc.id]?.laneCount || 1);
                                    return (
                                        <Pressable
                                            key={loc.id}
                                            testID={`LocationSchedule-Row-${loc.id}`}
                                            onPress={() => (selectable ? onToggleLocation?.(loc) : openLocation(loc.id))}
                                            style={[styles.labelCell, { height: rowH }, selected && styles.labelCellSelected]}
                                        >
                                            <Pressable
                                                testID={`LocationSchedule-LocationLink-${loc.id}`}
                                                onPress={() => openLocation(loc.id)}
                                                hitSlop={4}
                                                style={styles.labelLink}
                                                accessibilityRole="link"
                                            >
                                                <Text style={[styles.labelName, selected && styles.labelNameSelected]} numberOfLines={1}>
                                                    {loc.name}
                                                </Text>
                                                <Unicon name="open-outline" size={11} colorVariant="primary" />
                                            </Pressable>
                                            {!!loc.detailedArea && (
                                                <Text style={styles.labelSub} numberOfLines={1}>{loc.detailedArea}</Text>
                                            )}
                                        </Pressable>
                                    );
                                })}
                                {(!locations || locations.length === 0) && (
                                    <Text style={styles.emptyText}>No locations yet</Text>
                                )}
                            </View>

                            {/* Horizontally scrolling hour track */}
                            <ScrollView
                                ref={trackRef}
                                horizontal
                                showsHorizontalScrollIndicator={true}
                                style={styles.trackScroll}
                                onScroll={onTrackScroll}
                                scrollEventThrottle={16}
                                contentOffset={isWeb ? undefined : { x: DEFAULT_SCROLL_HOUR * HOUR_WIDTH, y: 0 }}
                                onLayout={(e) => { trackViewportRef.current = e.nativeEvent.layout.width; }}
                                testID="LocationSchedule-Track"
                            >
                                <View style={{ width: trackWidth }}>
                                    {(locations || []).map((loc) => {
                                        const selected = selectedSet.has(loc.id);
                                        const laneData = laneDataByLocation[loc.id] || { items: [], laneCount: 1 };
                                        const rowH = rowHeightFor(laneData.laneCount);
                                        return (
                                            <Pressable
                                                key={loc.id}
                                                disabled={!selectable}
                                                onPress={() => selectable && onToggleLocation?.(loc)}
                                                style={[styles.trackRow, { height: rowH, width: trackWidth }, selected && styles.trackRowSelected]}
                                            >
                                                {renderGridlines(rowH)}

                                                {highlightGeo && (
                                                    <View
                                                        testID={`LocationSchedule-Highlight-${loc.id}`}
                                                        style={[styles.highlight, { left: highlightGeo.left, width: highlightGeo.width }]}
                                                    />
                                                )}

                                                {laneData.items.map((item) => renderBlock(item, loc))}
                                            </Pressable>
                                        );
                                    })}
                                </View>
                            </ScrollView>
                        </View>
                    </ScrollView>
                </View>
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    container: {
        paddingHorizontal: spacing.sm,
        paddingBottom: spacing.lg,
    },
    navRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        marginBottom: spacing.xs,
    },
    navButton: {
        width: 40,
        height: 40,
        borderRadius: roundness.md,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.surfaceVariant,
        _web: {
            cursor: 'pointer',
            _hover: { backgroundColor: colors.hover },
        },
    },
    datePickerWrap: {
        flex: 1,
    },
    metaRow: {
        minHeight: 22,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: spacing.xs,
    },
    hint: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
    grid: {
        borderRadius: roundness.md,
        overflow: 'hidden',
        backgroundColor: colors.surface,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        backgroundColor: colors.surfaceVariant,
    },
    headerScroll: {
        flex: 1,
    },
    scrollArrow: {
        width: 26,
        alignItems: 'center',
        justifyContent: 'center',
        borderLeftWidth: 1,
        borderLeftColor: colors.border,
        _web: {
            cursor: 'pointer',
            _hover: { backgroundColor: colors.hover },
        },
    },
    cornerCell: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: spacing.sm,
        borderRightWidth: 1,
        borderRightColor: colors.border,
    },
    cornerArrow: {
        width: 22,
        height: 22,
        borderRadius: roundness.sm,
        alignItems: 'center',
        justifyContent: 'center',
        _web: {
            cursor: 'pointer',
            _hover: { backgroundColor: colors.hover },
        },
    },
    bodyScroll: {
        flexGrow: 0,
    },
    bodyRow: {
        flexDirection: 'row',
    },
    trackScroll: {
        flex: 1,
    },
    labelCol: {
        width: LABEL_WIDTH,
        borderRightWidth: 1,
        borderRightColor: colors.border,
    },
    labelCell: {
        justifyContent: 'center',
        paddingHorizontal: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    labelCellSelected: {
        backgroundColor: colors.eventsContainer,
        borderLeftWidth: 3,
        borderLeftColor: colors.events,
    },
    labelLink: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        alignSelf: 'flex-start',
        maxWidth: '100%',
        _web: {
            cursor: 'pointer',
        },
    },
    labelName: {
        fontSize: typography.caption,
        fontFamily: fonts.heading,
        color: colors.text,
        flexShrink: 1,
    },
    labelNameSelected: {
        color: colors.events,
    },
    labelSub: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    hourRow: {
        flexDirection: 'row',
        backgroundColor: colors.surfaceVariant,
    },
    hourCell: {
        justifyContent: 'center',
        paddingLeft: 6,
        borderRightWidth: 1,
        borderRightColor: colors.border,
    },
    hourText: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    trackRow: {
        position: 'relative',
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    trackRowSelected: {
        backgroundColor: colors.eventsContainer,
    },
    gridline: {
        position: 'absolute',
        top: 0,
        width: 1,
        backgroundColor: colors.border,
    },
    highlight: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        backgroundColor: colors.primaryContainer,
    },
    block: (color) => ({
        position: 'absolute',
        borderRadius: roundness.sm,
        backgroundColor: color ? color + '26' : colors.eventsContainer,
        justifyContent: 'center',
        paddingHorizontal: 6,
        overflow: 'hidden',
    }),
    blockText: (color) => ({
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: color || colors.events,
    }),
    emptyText: {
        padding: spacing.sm,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
}));

export default React.memo(LocationScheduleModal);
